import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Blurhash } from 'react-blurhash';
import './EntryImageGallery.css';

const MIN_SCALE = 1;
const MAX_SCALE = 4;
const SCALE_STEP = 0.5;
const SWIPE_THRESHOLD = 55;

function GalleryThumbnail({ image, index, isActive, onSelect }) {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(false);
  }, [image.src]);

  return (
    <button
      type="button"
      className={`entry-image-gallery__thumb${isActive ? ' is-active' : ''}`}
      title={`Open image ${index + 1}`}
      aria-label={`Open image ${index + 1}`}
      aria-pressed={isActive}
      onClick={() => onSelect(index)}
    >
      <span className="entry-image-gallery__thumb-inner">
        {!loaded && image.blurhash && (
          <Blurhash
            hash={image.blurhash}
            width={120}
            height={80}
            resolutionX={32}
            resolutionY={32}
            punch={1}
          />
        )}

        <img
          src={image.src}
          alt={image.alt || ''}
          loading="lazy"
          onLoad={() => setLoaded(true)}
          className={loaded ? 'is-loaded' : ''}
        />
      </span>
    </button>
  );
}

export default function EntryImageGallery({ images = [] }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [scale, setScale] = useState(MIN_SCALE);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const galleryRef = useRef(null);
  const pointerStartRef = useRef(null);
  const dragStartRef = useRef(null);
  const lastTapRef = useRef(0);

  const [canScrollPrevious, setCanScrollPrevious] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const imageCount = Array.isArray(images) ? images.length : 0;

  const updateGalleryScrollState = useCallback(() => {
    const gallery = galleryRef.current;
    if (!gallery) return;

    const maxScrollLeft = Math.max(0, gallery.scrollWidth - gallery.clientWidth);
    const tolerance = 2;

    setCanScrollPrevious(gallery.scrollLeft > tolerance);
    setCanScrollNext(gallery.scrollLeft < maxScrollLeft - tolerance);
  }, []);

  const scrollGallery = (direction) => {
    const gallery = galleryRef.current;
    if (!gallery) return;

    const firstThumbnail = gallery.querySelector('.entry-image-gallery__thumb');
    const thumbnailWidth = firstThumbnail?.getBoundingClientRect().width || 124;
    const gap = 10;
    const visibleCount = Math.max(1, Math.floor(gallery.clientWidth / (thumbnailWidth + gap)));
    const distance = Math.max(thumbnailWidth + gap, visibleCount * (thumbnailWidth + gap));

    gallery.scrollBy({
      left: direction * distance,
      behavior: 'smooth',
    });
  };

  const resetTransform = () => {
    setScale(MIN_SCALE);
    setPosition({ x: 0, y: 0 });
    dragStartRef.current = null;
    pointerStartRef.current = null;
  };

  const closeViewer = () => {
    setViewerOpen(false);
    resetTransform();
  };

  const showImage = (index) => {
    if (!imageCount) return;
    const nextIndex = (index + imageCount) % imageCount;
    setActiveIndex(nextIndex);
    resetTransform();
  };

  const showPrevious = () => showImage(activeIndex - 1);
  const showNext = () => showImage(activeIndex + 1);

  useEffect(() => {
    setActiveIndex(0);
    setViewerOpen(false);
    resetTransform();
  }, [images]);

  useEffect(() => {
    const gallery = galleryRef.current;
    if (!gallery) return undefined;

    gallery.scrollLeft = 0;
    updateGalleryScrollState();

    const handleScroll = () => updateGalleryScrollState();
    const handleResize = () => updateGalleryScrollState();

    gallery.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);

    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(handleResize)
        : null;
    resizeObserver?.observe(gallery);

    return () => {
      gallery.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      resizeObserver?.disconnect();
    };
  }, [images, updateGalleryScrollState]);

  useEffect(() => {
    if (!viewerOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeViewer();
      } else if (event.key === 'ArrowLeft' && imageCount > 1) {
        event.preventDefault();
        showPrevious();
      } else if (event.key === 'ArrowRight' && imageCount > 1) {
        event.preventDefault();
        showNext();
      } else if (event.key === '+' || event.key === '=') {
        event.preventDefault();
        setScale((value) => Math.min(MAX_SCALE, value + SCALE_STEP));
      } else if (event.key === '-') {
        event.preventDefault();
        setScale((value) => {
          const next = Math.max(MIN_SCALE, value - SCALE_STEP);
          if (next === MIN_SCALE) setPosition({ x: 0, y: 0 });
          return next;
        });
      } else if (event.key === '0') {
        event.preventDefault();
        resetTransform();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [viewerOpen, activeIndex, imageCount]);

  if (!Array.isArray(images) || images.length === 0) return null;

  const activeImage = images[activeIndex] || images[0];

  const handleSelect = (index) => {
    setActiveIndex(index);
    resetTransform();
    setViewerOpen(true);
  };

  const handleBackdropClick = (event) => {
    if (event.target === event.currentTarget) {
      closeViewer();
    }
  };

  const zoomIn = () => {
    setScale((value) => Math.min(MAX_SCALE, value + SCALE_STEP));
  };

  const zoomOut = () => {
    setScale((value) => {
      const next = Math.max(MIN_SCALE, value - SCALE_STEP);
      if (next === MIN_SCALE) setPosition({ x: 0, y: 0 });
      return next;
    });
  };

  const toggleZoom = () => {
    if (scale > MIN_SCALE) {
      resetTransform();
    } else {
      setScale(2);
    }
  };

  const handlePointerDown = (event) => {
    event.currentTarget.setPointerCapture?.(event.pointerId);

    pointerStartRef.current = {
      x: event.clientX,
      y: event.clientY,
      time: Date.now(),
    };

    if (scale > MIN_SCALE) {
      dragStartRef.current = {
        x: event.clientX,
        y: event.clientY,
        positionX: position.x,
        positionY: position.y,
      };
    }
  };

  const handlePointerMove = (event) => {
    if (scale <= MIN_SCALE || !dragStartRef.current) return;

    const deltaX = event.clientX - dragStartRef.current.x;
    const deltaY = event.clientY - dragStartRef.current.y;

    setPosition({
      x: dragStartRef.current.positionX + deltaX,
      y: dragStartRef.current.positionY + deltaY,
    });
  };

  const handlePointerUp = (event) => {
    event.currentTarget.releasePointerCapture?.(event.pointerId);

    const start = pointerStartRef.current;
    const dragged = dragStartRef.current;
    pointerStartRef.current = null;
    dragStartRef.current = null;

    if (!start || scale > MIN_SCALE || dragged) return;

    const deltaX = event.clientX - start.x;
    const deltaY = event.clientY - start.y;
    const elapsed = Date.now() - start.time;

    if (
      imageCount > 1 &&
      elapsed < 700 &&
      Math.abs(deltaX) >= SWIPE_THRESHOLD &&
      Math.abs(deltaX) > Math.abs(deltaY)
    ) {
      if (deltaX < 0) showNext();
      else showPrevious();
      return;
    }

    if (Math.abs(deltaX) < 10 && Math.abs(deltaY) < 10) {
      const now = Date.now();
      if (now - lastTapRef.current < 300) {
        toggleZoom();
        lastTapRef.current = 0;
      } else {
        lastTapRef.current = now;
      }
    }
  };

  const handleWheel = (event) => {
    event.preventDefault();

    if (event.deltaY < 0) {
      zoomIn();
    } else {
      zoomOut();
    }
  };

  return (
    <>
      <div className="entry-image-gallery-shell">
        {imageCount > 1 && (
          <button
            type="button"
            className="entry-image-gallery__scroll-button entry-image-gallery__scroll-button--previous"
            aria-label="Scroll to previous images"
            onClick={() => scrollGallery(-1)}
            disabled={!canScrollPrevious}
          >
            &#8249;
          </button>
        )}

        <div
          ref={galleryRef}
          className="entry-image-gallery"
          aria-label={`Entry images, ${imageCount} total`}
        >
          {images.map((image, index) => (
            <GalleryThumbnail
              key={image.id ?? image.src ?? index}
              image={image}
              index={index}
              isActive={index === activeIndex}
              onSelect={handleSelect}
            />
          ))}
        </div>

        {imageCount > 1 && (
          <button
            type="button"
            className="entry-image-gallery__scroll-button entry-image-gallery__scroll-button--next"
            aria-label="Scroll to next images"
            onClick={() => scrollGallery(1)}
            disabled={!canScrollNext}
          >
            &#8250;
          </button>
        )}

        {imageCount > 1 && (
          <div className="entry-image-gallery__total">{imageCount} images</div>
        )}
      </div>

      {viewerOpen && activeImage && (
        <div
          className="entry-image-viewer"
          role="dialog"
          aria-modal="true"
          aria-label={`Image ${activeIndex + 1} of ${images.length}`}
          onMouseDown={handleBackdropClick}
        >
          <button
            type="button"
            className="entry-image-viewer__close"
            aria-label="Close image viewer"
            onClick={closeViewer}
          >
            &times;
          </button>

          {imageCount > 1 && (
            <>
              <button
                type="button"
                className="entry-image-viewer__nav entry-image-viewer__nav--previous"
                aria-label="View previous image"
                onClick={showPrevious}
              >
                &#8249;
              </button>

              <button
                type="button"
                className="entry-image-viewer__nav entry-image-viewer__nav--next"
                aria-label="View next image"
                onClick={showNext}
              >
                &#8250;
              </button>
            </>
          )}

          <div
            className={`entry-image-viewer__stage${
              scale > MIN_SCALE ? ' is-zoomed' : ''
            }`}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            onWheel={handleWheel}
          >
            <img
              className="entry-image-viewer__image"
              src={activeImage.fullSrc || activeImage.src}
              alt={activeImage.alt || `Entry image ${activeIndex + 1}`}
              draggable="false"
              onDoubleClick={toggleZoom}
              style={{
                transform: `translate3d(${position.x}px, ${position.y}px, 0) scale(${scale})`,
              }}
            />
          </div>

          <div className="entry-image-viewer__zoom-controls" aria-label="Zoom controls">
            <button
              type="button"
              aria-label="Zoom out"
              onClick={zoomOut}
              disabled={scale <= MIN_SCALE}
            >
              &minus;
            </button>
            <button
              type="button"
              className="entry-image-viewer__zoom-level"
              aria-label="Reset zoom"
              onClick={resetTransform}
            >
              {Math.round(scale * 100)}%
            </button>
            <button
              type="button"
              aria-label="Zoom in"
              onClick={zoomIn}
              disabled={scale >= MAX_SCALE}
            >
              +
            </button>
          </div>

          {images.length > 1 && (
            <div className="entry-image-viewer__counter">
              {activeIndex + 1} of {images.length}
            </div>
          )}
        </div>
      )}
    </>
  );
}
